import { z } from "zod";

import {
  positiveIntegerSchema,
  stringArraySchema,
  type TArticleFilters,
  type TDrawerFilters,
  type TFilters,
  type TModalFilters,
  type TSheetFilters,
  type TTabFilters,
} from "./filters.schema";

/*-------------------------------------
|  Types
|-------------------------------------*/

export type {
  TArticleFilters,
  TDrawerDataFilterKey,
  TDrawerFilter,
  TDrawerFilters,
  TFilters,
  TModalDataFilterKey,
  TModalFilter,
  TModalFilters,
  TSheetDataFilterKey,
  TSheetFilter,
  TSheetFilters,
  TTabFilterKey,
  TTabFilters,
} from "./filters.schema";

export type TStoreFilterKey = {
  [TFilterGroup in keyof TFilters]: Extract<
    keyof TFilters[TFilterGroup],
    `${string}:${string}`
  >;
}[keyof TFilters];

export type TApiFilterKey<
  TFilter extends TStoreFilterKey = TStoreFilterKey,
> = TFilter extends `${string}:${infer TKey}` ? TKey : never;

export type TFilterConfig<TValue> = {
  defaultValue: TValue;
  schema: z.ZodType<TValue>;
};

export type TConfiguredFilterGroup = Exclude<
  keyof TFilters,
  "sheets" | "drawers" | "modals" | "tabs"
>;

type TFiltersConfig = {
  [TFilterGroup in TConfiguredFilterGroup]: {
    [TFilter in keyof TFilters[TFilterGroup]]: TFilterConfig<
      TFilters[TFilterGroup][TFilter]
    >;
  };
};

export type TRuntimeFilterConfig = TFilterConfig<unknown>;

export type TFiltersStore = {
  filters: TFilters;
};

export type TFilterUpdateArgs = {
  [TFilterGroup in keyof TFilters]: {
    [TFilter in keyof TFilters[TFilterGroup]]: [
      filter: TFilter,
      value: TFilters[TFilterGroup][TFilter],
    ];
  }[keyof TFilters[TFilterGroup]];
}[keyof TFilters];

export type TFiltersStoreDispatch = {
  hydrateFilters(filters: Partial<TFilters>): void;
  replaceFilterGroup<TFilterGroup extends keyof TFilters>(
    filterGroup: TFilterGroup,
    filters: TFilters[TFilterGroup],
  ): void;
  updateFilters(...args: TFilterUpdateArgs): void;
};

export type TUseFiltersStore = {
  filters: TFilters;
  replaceFilterGroup<TFilterGroup extends keyof TFilters>(
    filterGroup: TFilterGroup,
    filters: TFilters[TFilterGroup],
  ): void;
  updateFilters(...args: TFilterUpdateArgs): void;
};

/*-------------------------------------
|  Factories
|-------------------------------------*/

const positiveIntegerFilter = (defaultValue = 1) => ({
  defaultValue,
  schema: positiveIntegerSchema,
});

const stringArrayFilter = (defaultValue: string[] = []) => ({
  defaultValue,
  schema: stringArraySchema,
});

const stringFilter = (defaultValue = "") => ({
  defaultValue,
  schema: z.string().min(1),
});

/*-------------------------------------
|  Config
|-------------------------------------*/

/**
 * Prefix all filters with the client path,
 * this means that in future we can store the state for
 *  multiple on a single page if needed
 * */

export const FILTERS_CONFIG = {
  articles: {
    "articles:page": positiveIntegerFilter(),
    "articles:categories": stringArrayFilter(),
  },
} satisfies TFiltersConfig;

export const TAB_FILTER_CONFIG = stringFilter();
export const SHEET_FILTER_CONFIG = stringFilter();
export const DRAWER_FILTER_CONFIG = stringFilter();
export const MODAL_FILTER_CONFIG = stringFilter();

/*-------------------------------------
|  Defaults
|-------------------------------------*/

export const DEFAULT_ARTICLE_FILTERS: TArticleFilters = {
  "articles:page": FILTERS_CONFIG.articles["articles:page"].defaultValue,
  "articles:categories": FILTERS_CONFIG.articles["articles:categories"].defaultValue,
};

export const DEFAULT_TAB_FILTERS: TTabFilters = {};

export const DEFAULT_SHEET_FILTERS: TSheetFilters = {};
export const DEFAULT_DRAWER_FILTERS: TDrawerFilters = {};
export const DEFAULT_MODAL_FILTERS: TModalFilters = {};

export const DEFAULT_FILTERS: TFilters = {
  articles: DEFAULT_ARTICLE_FILTERS,
  sheets: DEFAULT_SHEET_FILTERS,
  drawers: DEFAULT_DRAWER_FILTERS,
  modals: DEFAULT_MODAL_FILTERS,
  tabs: DEFAULT_TAB_FILTERS,
};
